// =====================================================================
// src/screens/super-admin/lib/subscriptionConfig.js
//
// Fetches platform subscription config from /config/subscription. Read
// by the Activation modal to render the tier options (₹199/₹499/₹899).
//
// Design decision (banked): keep pricing in a Firestore doc rather than
// a code constant, so raising prices or adding tiers is a Firebase Console
// edit, not a code deploy. Since we don't have an in-panel Settings UI
// yet, admin edits the doc directly. FALLBACK_TIERS below is used if the
// doc doesn't exist yet — lets the modal render on fresh installs.
//
// The rule at /config/{configId} is admin-only (Session 6a) — this
// helper only works when called from the admin panel.
// =====================================================================

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

// Fallback tiers used when /config/subscription doc doesn't exist. Matches
// the launch pricing so a fresh dev install works without needing the
// Firestore doc created first. Once the doc exists in prod, this is
// effectively dead code — but keeping it as a safety net for future
// environments and one-off tests.
export const FALLBACK_TIERS = [
  { id: '199', amount: 199, durationMonths: 1, label: '1 month' },
  { id: '499', amount: 499, durationMonths: 3, label: '3 months' },
  { id: '899', amount: 899, durationMonths: 6, label: '6 months' },
];

// ============================================================
// loadSubscriptionConfig()
// ============================================================
//
// Reads /config/subscription. Returns:
//   {
//     tiers:      Array<Tier>,   // never empty — falls back to FALLBACK_TIERS
//     isFallback: boolean,       // true if doc missing / had no tiers
//   }
//
// Each Tier: { id, amount, durationMonths, label }
//   - id: stable string identifier ('199' | '499' | '899' by default,
//     but callers should treat it as opaque — admin can add new tiers
//     with different ids in the config doc)
//   - amount: numeric ₹ price
//   - durationMonths: numeric months of validity
//   - label: user-visible duration ("1 month", "3 months", etc.)
//
// Firestore cost: 1 read.
//
// The caller in ActivationModal loads this once when the modal opens
// and never re-fetches within a single modal open — 1 read per
// activation session, not per keystroke.
export async function loadSubscriptionConfig() {
  const ref = doc(db, 'config', 'subscription');
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return { tiers: FALLBACK_TIERS, isFallback: true };
  }

  const data = snap.data() || {};
  const tiers = Array.isArray(data.tiers) && data.tiers.length > 0
    ? data.tiers
    : FALLBACK_TIERS;

  return {
    tiers,
    isFallback: !Array.isArray(data.tiers) || data.tiers.length === 0,
  };
}
